/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * The component for page BreadCrumbs
 *
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @author Evgenij Tyan
 * @author Julia Ignatieva
 * @version 1.12.0
 * @since 1.1.0
 */

import React, { FC, useCallback } from 'react';

import { Link } from 'react-router-dom';

import { icons } from '../../../assets';
import { ImgStyled } from '../../../components/common/Block/Img';
import { RoutePath } from '../../../routes/constants/routesConstants';
import GTMOperations from '../../analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../analytics/GoogleTagManager/types/GTMActionTypes';
import { ICommonTranslation, ICorporateTranslation } from '../../internationalization/types';
import { IBreadCrumb } from '../breadCrumbsTypes';
import { BreadCrumbsContainerStyled, BreadCrumbsLayoutStyled } from './styled/BreadCrumbsStyled';

interface IBreadCrumbsProps {
    data: IBreadCrumb[] | undefined;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    translation: ICommonTranslation & ICorporateTranslation;
}

export const BreadCrumbs: FC<IBreadCrumbsProps> = (props) => {
    const {
        pushGtmAnalytic,
    } = props;

    const clickOnBreadCrumb = useCallback((item: string) => {
        pushGtmAnalytic({
            operation: GTMOperations.navigation.clickOnBreadCrumbs(item),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [pushGtmAnalytic]);

    return props.data?.length ?
        (
            <BreadCrumbsLayoutStyled>
                <BreadCrumbsContainerStyled>
                    <Link
                        className='bread-crumbs__link'
                        to={RoutePath.main}
                        onClick={() => clickOnBreadCrumb(props.translation.pages.dashboard)}
                        onMouseDown={(event: React.MouseEvent) => {
                            if (event.button !== 1) return;
                            clickOnBreadCrumb(props.translation.pages.dashboard);
                        }}
                    >
                        {props.translation.pages.dashboard}
                    </Link>
                </BreadCrumbsContainerStyled>
                {
                    props.data.map(breadCrumb => (
                            <BreadCrumbsContainerStyled key={breadCrumb.title}>
                                <ImgStyled
                                    height='10px'
                                    src={icons.arrowForward}
                                    width='20px'
                                />
                                {
                                    breadCrumb.url ? (
                                        breadCrumb.isUrlExternal ?
                                            <a className='bread-crumbs__link'
                                               href={breadCrumb.url}
                                               onClick={(event: React.MouseEvent) => {
                                                   breadCrumb.onClick?.(event)
                                               }}
                                               onMouseDown={(event: React.MouseEvent) => {
                                                   if (event.button !== 1) return;
                                                   breadCrumb.onClick?.(event);
                                               }}
                                            >
                                                {breadCrumb.title}
                                            </a>
                                            :
                                            <Link
                                                className='bread-crumbs__link'
                                                to={breadCrumb.url}
                                                onClick={(event: React.MouseEvent) => {
                                                    breadCrumb.onClick?.(event)
                                                }}
                                                onMouseDown={(event: React.MouseEvent) => {
                                                    if (event.button !== 1) return;
                                                    breadCrumb.onClick?.(event);
                                                }}
                                            >
                                                {breadCrumb.title}
                                            </Link>
                                    ) : breadCrumb.title
                                }
                            </BreadCrumbsContainerStyled>
                        ),
                    )
                }
            </BreadCrumbsLayoutStyled>
        ) : null;
};
